import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    CircularProgress,
    TextField,
    Box,
    TableSortLabel,
} from "@mui/material";



const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 25];

const SEARCH_FIELD_SX = {
    width: 250,
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        transition: "all 0.2s ease-in-out",

        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--text-primary)",
        },

        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--color-primary)",
            borderWidth: "2px",
        },
    },

    "& .MuiInputBase-input": {
        color: "var(--text-primary)",
    },

    "& .MuiInputLabel-root.Mui-focused": {
        color: "var(--text-primary)",
    },
};

const TABLE_HEADER_CELL_SX = {
    fontWeight: "bold",
    backgroundColor: "var(--bg-card)",
    color: "var(--text-primary)",
};

const PAPER_SX = {
    borderRadius: "4px",
    boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
    p: 2,
    overflow: "hidden",
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
};

const sortData = (data, sortConfig) => {
    if (!sortConfig.field) return data;

    return [...data].sort((a, b) => {
        const aValue = getNestedValue(a, sortConfig.field);
        const bValue = getNestedValue(b, sortConfig.field);

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
    });
};

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

const useControlledState = (controlledValue, defaultValue, onChangeCallback) => {
    const isControlled = controlledValue !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);

    const value = isControlled ? controlledValue : uncontrolledValue;

    const setValue = useCallback(
        (newValue) => {
            if (!isControlled) {
                setUncontrolledValue(newValue);
            }
            if (onChangeCallback) {
                onChangeCallback(newValue);
            }
        },
        [isControlled, onChangeCallback]
    );

    return [value, setValue];
};

const useSorting = () => {
    const [sortConfig, setSortConfig] = useState({ field: null, direction: "asc" });

    const handleSort = useCallback((field) => {
        setSortConfig((prev) => ({
            field,
            direction: prev.field === field && prev.direction === "asc" ? "desc" : "asc",
        }));
    }, []);

    return { sortConfig, handleSort };
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SearchField = React.memo(({ value, onChange, enabled }) => {
    if (!enabled) return null;

    return (
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", borderRadius: 4 }}>
            <TextField
                variant="outlined"
                size="small"
                placeholder="Procurar..."
                value={value}
                onChange={onChange}
                sx={SEARCH_FIELD_SX}
            />
        </Box>
    );
});

SearchField.displayName = "SearchField";

const TableHeader = React.memo(({ columns, sortConfig, onSort }) => (
    <TableHead>
        <TableRow>
            {columns.map((col) => (
                <TableCell
                    key={col.field}
                    align={col.align || "left"}
                    sx={TABLE_HEADER_CELL_SX}
                    sortDirection={sortConfig.field === col.field ? sortConfig.direction : false}
                >
                    {col.sortable ? (
                        <TableSortLabel
                            active={sortConfig.field === col.field}
                            direction={sortConfig.field === col.field ? sortConfig.direction : "asc"}
                            onClick={() => onSort(col.field)}
                        >
                            {col.label}
                        </TableSortLabel>
                    ) : (
                        col.label
                    )}
                </TableCell>
            ))}
        </TableRow>
    </TableHead>
));

TableHeader.displayName = "TableHeader";

const LoadingRow = ({ colSpan }) => (
    <TableRow>
        <TableCell colSpan={colSpan} align="center">
            <CircularProgress disableShrink sx={{ color: "var(--color-primary)" }} size={40} />
        </TableCell>
    </TableRow>
);

LoadingRow.displayName = "LoadingRow";

const EmptyRow = ({ colSpan }) => (
    <TableRow>
        <TableCell colSpan={colSpan} align="center">
            Nenhum dado encontrado
        </TableCell>
    </TableRow>
);

EmptyRow.displayName = "EmptyRow";

const DataRow = ({ row, columns, index, isSelected, onSelect }) => {
    const rowSx = {
        cursor: "pointer",
        fontWeight: isSelected ? "bold" : "normal",
        backgroundColor: isSelected ? "var(--tab-active-bg)" : "inherit",
        transition: "background-color 0.2s ease-in-out",
    };

    return (
        <TableRow
            hover
            onClick={() => onSelect(index)}
            sx={rowSx}
        >
            {columns.map((col) => {
                const value = getNestedValue(row, col.field);
                return (
                    <TableCell
                        key={col.field}
                        align={col.align || "left"}
                        sx={{ pl: col.paddingLeft || 2 }}
                    >
                        {col.render ? col.render(value, row) : value}
                    </TableCell>
                );
            })}
        </TableRow>
    );
};

DataRow.displayName = "DataRow";

const TableContent = ({
    loading,
    columns,
    data,
    selectedRow,
    onRowSelect
}) => {
    if (loading) {
        return <LoadingRow colSpan={columns.length} />;
    }

    if (data.length === 0) {
        return <EmptyRow colSpan={columns.length} />;
    }

    return data.map((row, index) => (
        <DataRow
            key={row.id || index}
            row={row}
            columns={columns}
            index={index}
            isSelected={selectedRow === index}
            onSelect={onRowSelect}
        />
    ));
};

TableContent.displayName = "TableContent";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const HSTable = ({
    columns,
    data = [],
    rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
    loading = false,
    page: pageProp,
    rowsPerPage: rowsPerPageProp,
    onPageChange,
    onRowsPerPageChange,
    totalCount,
    onSearchChange,
    searchValue: searchValueProp,
    enableSearch = false,
}) => {

    const [page, setPage] = useControlledState(
        pageProp,
        0,
        onPageChange
    );

    const [rowsPerPage, setRowsPerPage] = useControlledState(
        rowsPerPageProp,
        rowsPerPageOptions[0],
        onRowsPerPageChange
    );

    const [searchValue, setSearchValue] = useControlledState(
        searchValueProp,
        "",
        onSearchChange
    );

    const [selectedRow, setSelectedRow] = useState(null);
    const { sortConfig, handleSort } = useSorting();

    const sortedData = useMemo(
        () => sortData(data, sortConfig),
        [data, sortConfig]
    );

    const handleChangePage = useCallback(
        (_, newPage) => {
            setPage(newPage);
        },
        [setPage]
    );

    const handleChangeRowsPerPage = useCallback(
        (event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            setRowsPerPage(newRowsPerPage);
            setPage(0);
        },
        [setRowsPerPage, setPage]
    );

    const handleSearchChange = useCallback(
        (event) => {
            setSearchValue(event.target.value);
        },
        [setSearchValue]
    );

    const handleRowSelect = useCallback((index) => {
        setSelectedRow(index);
    }, []);

    return (
        <Paper sx={PAPER_SX}>
            <SearchField
                value={searchValue}
                onChange={handleSearchChange}
                enabled={enableSearch}
            />

            <TableContainer>
                <Table>
                    <TableHeader
                        columns={columns}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                    />
                    <TableBody>
                        <TableContent
                            loading={loading}
                            columns={columns}
                            data={sortedData}
                            selectedRow={selectedRow}
                            onRowSelect={handleRowSelect}
                        />
                    </TableBody>
                </Table>
            </TableContainer>

            {!loading && (
                <TablePagination
                    component="div"
                    count={totalCount ?? data.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                    labelRowsPerPage="Linhas por página:"
                />
            )}
        </Paper>
    );
}

HSTable.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            align: PropTypes.oneOf(["left", "right", "center"]),
            sortable: PropTypes.bool,
            render: PropTypes.func,
            paddingLeft: PropTypes.number,
        })
    ).isRequired,
    data: PropTypes.array,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    loading: PropTypes.bool,
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
    onPageChange: PropTypes.func,
    onRowsPerPageChange: PropTypes.func,
    totalCount: PropTypes.number,
    onSearchChange: PropTypes.func,
    searchValue: PropTypes.string,
    enableSearch: PropTypes.bool,
};

HSTable.defaultProps = {
    data: [],
    rowsPerPageOptions: [5, 10, 25],
    loading: false,
    enableSearch: false,
};

export default HSTable;